import type { Query } from '@cubejs-client/core';

export const DEFAULT_QUERY_TEXT = '';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export interface CubeLoadResult {
  rawData: () => unknown[];
  decompose: () => CubeLoadResult[];
}

export type LoadCubeQuery = (query: Query) => Promise<CubeLoadResult>;

export type QueryParseResult =
  | { query: Query; errorKey: null }
  | { query: null; errorKey: 'dev.cubeQueryRunner.errors.invalidJson' | 'dev.cubeQueryRunner.errors.notObject' };

export function extractRows(result: CubeLoadResult): unknown[] {
  return result.decompose().flatMap(decomposed => decomposed.rawData());
}

export function isCubeQuery(value: unknown): value is Query {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function compactQueryFilters(query: Query): Query {
  if (!Array.isArray(query.filters)) return query;
  return { ...query, filters: query.filters.filter(queryFilter => queryFilter !== null && queryFilter !== undefined) };
}

export function parseCubeQueryText(queryText: string): QueryParseResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(queryText);
  } catch {
    return { query: null, errorKey: 'dev.cubeQueryRunner.errors.invalidJson' };
  }

  if (!isCubeQuery(parsed)) {
    return { query: null, errorKey: 'dev.cubeQueryRunner.errors.notObject' };
  }

  return { query: compactQueryFilters(parsed), errorKey: null };
}

export function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export const formatDuration = (durationMs: number | undefined, unavailableLabel: string): string =>
  durationMs === undefined ? unavailableLabel : `${(durationMs / 1000).toFixed(1)} s`;

export const formatElapsedRecordingTime = (recordingStartedAt: number | null, currentTime = Date.now()): string => {
  if (recordingStartedAt === null) return '00:00:00';

  const elapsedSeconds = Math.max(0, Math.floor((currentTime - recordingStartedAt) / 1000));
  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;

  return [hours, minutes, seconds].map(timeUnit => timeUnit.toString().padStart(2, '0')).join(':');
};

export const getResponseRowCount = (data: unknown): number | undefined => {
  if (Array.isArray(data)) return data.length;
  if (!isRecord(data) || !Array.isArray(data.data)) return undefined;
  return data.data.length;
};
